import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { ProductionKanban } from "@/components/workshop/ProductionKanban";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

interface ProductionOrder {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  itemCount: number;
  promisedDeliveryAt?: string;
  customer: {
    publicId: string;
    name: string;
  };
}

export default function ProductionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<ProductionOrder[]>([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    setLoading(true);
    try {
      const response = await fetch(`/api/workshop/orders?stage=production`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }

  async function handleStartProduction(orderId: string) {
    try {
      const response = await fetch(
        `/api/workshop/orders/${orderId}/production/start`,
        { method: "POST" }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to start production");
      }

      toast.success("Production started");
      fetchOrders();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to start production");
    }
  }

  async function handleMarkQc(orderId: string) {
    try {
      const response = await fetch(
        `/api/workshop/orders/${orderId}/qc/mark`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ to: "QC" }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to mark for QC");
      }

      toast.success("Order marked for quality check");
      fetchOrders();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to mark for QC");
    }
  }

  async function handleMarkReady(orderId: string) {
    try {
      const response = await fetch(
        `/api/workshop/orders/${orderId}/qc/mark`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ to: "READY_FOR_DELIVERY" }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to mark ready");
      }

      toast.success("Order ready for delivery");
      fetchOrders();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to mark ready");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Production"
        actions={
          <Button variant="outline" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {loading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <ProductionKanban
          orders={orders}
          onStartProduction={handleStartProduction}
          onMarkQc={handleMarkQc}
          onMarkReady={handleMarkReady}
          onViewOrder={(id) => navigate(`/workshop/orders/${id}`)}
        />
      )}
    </div>
  );
}
